import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

// Helper functions for persistent storage (PWA compatible)
const TOKEN_KEY = 'era_lodge_token';
const USER_KEY = 'era_lodge_user';

const saveToStorage = (key, value) => {
  try {
    localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
    // Also save to sessionStorage as backup
    sessionStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  } catch (e) {
    console.warn('Storage save error:', e);
  }
};

const getFromStorage = (key, isJSON = false) => {
  try {
    // Try localStorage first, then sessionStorage as fallback
    let value = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (value && isJSON) {
      return JSON.parse(value);
    }
    return value;
  } catch (e) {
    console.warn('Storage read error:', e);
    return null;
  }
};

const removeFromStorage = (key) => {
  try {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  } catch (e) {
    console.warn('Storage remove error:', e);
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize user from storage for instant UI (no flicker)
  const [user, setUser] = useState(() => getFromStorage(USER_KEY, true));
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = getFromStorage(TOKEN_KEY);
    
    if (token) {
      // Ensure token is in localStorage for API interceptor
      localStorage.setItem('token', token);
      
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
        saveToStorage(USER_KEY, response.data);
      } catch (error) {
        // Only clear on actual auth errors, not network errors
        if (error.response?.status === 401 || error.response?.status === 403) {
          removeFromStorage(TOKEN_KEY);
          removeFromStorage(USER_KEY);
          localStorage.removeItem('token');
          setUser(null);
        } else {
          // Network error - keep user logged in with cached data
          const cachedUser = getFromStorage(USER_KEY, true);
          if (cachedUser) {
            setUser(cachedUser);
          }
        }
      }
    } else {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, user: userData } = response.data;
    // Save to all storage mechanisms
    saveToStorage(TOKEN_KEY, token);
    saveToStorage(USER_KEY, userData);
    localStorage.setItem('token', token); // For API interceptor
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await api.post('/auth/register', { name, email, password });
    const { token, user: userData } = response.data;
    // Save to all storage mechanisms
    saveToStorage(TOKEN_KEY, token);
    saveToStorage(USER_KEY, userData);
    localStorage.setItem('token', token); // For API interceptor
    setUser(userData);
    return userData;
  };

  const logout = () => {
    removeFromStorage(TOKEN_KEY);
    removeFromStorage(USER_KEY);
    localStorage.removeItem('token');
    setUser(null);
  };

  const forgotPassword = async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  };

  const resetPassword = async (token, newPassword) => {
    const response = await api.post('/auth/reset-password', { token, new_password: newPassword });
    return response.data;
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
